import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useSession, useToast } from '../lib/store';
import type { CommunityProject, ContributeResult, SeasonSummary } from '../lib/types';
import { GiveForm } from './GiveForm';
import { Bar, Button, Panel } from './ui';
import { formatDuration, formatNumber } from '../lib/format';

/**
 * The Community Project.
 *
 * One shared goal, one meter, no map. This is what habitat restoration became:
 * the mechanics that were worth keeping — a server-wide bar, server-priced
 * contributions, milestone rewards paid to everyone who helped — with the ten
 * locations and the page you had to travel to taken out.
 *
 * Deliberately a component rather than a page. It belongs *inside* Community
 * and on Home, because a shared goal that lives on its own screen is a screen
 * most players never open. `compact` is the Home version: the meter, the
 * headline and a way in, and nothing else.
 */
export function CommunityProjectPanel({
  project,
  season,
  compact = false,
  onChange,
}: {
  project: CommunityProject | null;
  season?: SeasonSummary;
  compact?: boolean;
  onChange?: (project: CommunityProject) => void;
}) {
  const { push } = useToast();
  const { setWallet, celebrate, celebrateBadges, refresh } = useSession();
  const [giving, setGiving] = useState(false);

  if (!project) {
    return (
      <Panel title="◈ Community Project" raised={false}>
        <p className="tiny muted" style={{ margin: 0 }}>
          Nothing is running at the moment. A new project starts with the next season.
        </p>
      </Panel>
    );
  }

  async function contribute(gift: {
    coins: number;
    items: Array<{ itemKey: string; quantity: number }>;
    pokemonIds: string[];
  }) {
    try {
      const result = await api.post<ContributeResult>('/community-projects/contribute', gift);
      setWallet(result.wallet);
      celebrate(result.unlocked ?? []);
      celebrateBadges(result.badges ?? []);
      if (result.project) onChange?.(result.project);

      push('reward', `+${formatNumber(result.pointsAdded)} to ${project!.name}`, '◈');
      if (result.capped) {
        push('info', "That was more than today's allowance, so only part of it counted.");
      }
      for (const milestone of result.crossed) {
        push('reward', `${milestone.name} — ${milestone.description}`, '✦');
      }
      setGiving(false);
      void refresh();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not contribute.');
    }
  }

  const percent = Math.round(project.ratio * 100);

  return (
    <Panel
      title="◈ Community Project"
      hint={season ? `Season ends in ${formatDuration(season.secondsRemaining)}` : undefined}
      raised={false}
      action={
        compact ? (
          <Link to="/community" className="btn btn--sm">
            Help out
          </Link>
        ) : undefined
      }
    >
      <div className="stack-sm">
        <div>
          <h3 className="project__name">{project.name}</h3>
          <p className="tiny muted" style={{ margin: 0 }}>
            {project.tagline}
          </p>
        </div>

        {/* The meter is the whole feature. Big, labelled with the actual
            numbers, and showing the milestone ticks so the next rung is a
            visible target rather than a surprise. */}
        <div className="project__meter">
          <Bar ratio={project.ratio} tall label={`${percent}%`} />
          <div className="project__ticks" aria-hidden>
            {project.milestones.map((milestone) => (
              <span
                key={milestone.step}
                className={`project__tick ${milestone.reached ? 'project__tick--reached' : ''}`}
                style={{ left: `${Math.min(100, (milestone.threshold / project.goal) * 100)}%` }}
                title={milestone.name}
              />
            ))}
          </div>
        </div>

        <div className="row row--between tiny">
          <span className="mono">
            {formatNumber(project.points)} / {formatNumber(project.goal)}
          </span>
          <span className="muted">
            {formatNumber(project.contributors)}{' '}
            {project.contributors === 1 ? 'trainer has' : 'trainers have'} helped
          </span>
        </div>

        {compact ? (
          project.you.points > 0 ? (
            <p className="tiny muted" style={{ margin: 0 }}>
              You have given {formatNumber(project.you.points)} points to this.
            </p>
          ) : (
            <p className="tiny muted" style={{ margin: 0 }}>
              Every egg you hatch counts towards this automatically.
            </p>
          )
        ) : (
          <>
            <p className="tiny" style={{ margin: 0 }}>
              {project.description}
            </p>

            <ol className="project__ladder">
              {project.milestones.map((milestone) => (
                <li
                  key={milestone.step}
                  className={`project__rung ${milestone.reached ? 'project__rung--reached' : ''}`}
                >
                  <span className="project__rung-mark" aria-hidden />
                  <span className="project__rung-body">
                    <strong className="tiny">{milestone.name}</strong>
                    <span className="tiny muted">
                      {formatNumber(milestone.threshold)}
                      {milestone.rewardCoins > 0 && ` · +${formatNumber(milestone.rewardCoins)} coins each`}
                      {milestone.rewardTitle && ` · title: ${milestone.rewardTitle}`}
                    </span>
                  </span>
                </li>
              ))}
            </ol>

            <div className="pxwell stack-sm" style={{ padding: 'var(--gap-sm)' }}>
              <div className="row row--between tiny">
                <span className="mono muted">YOUR CONTRIBUTION</span>
                <span className="mono">{formatNumber(project.you.points)}</span>
              </div>
              <div className="tiny muted">
                {project.you.remainingToday > 0
                  ? `${formatNumber(project.you.remainingToday)} of today's ${formatNumber(project.you.dailyCap)} allowance left.`
                  : "You've given everything you can today — it resets at midnight UTC."}
              </div>
              {!giving && !project.isComplete && (
                <Button
                  size="sm"
                  variant="go"
                  onClick={() => setGiving(true)}
                  disabled={project.you.remainingToday <= 0}
                >
                  Contribute
                </Button>
              )}
              {project.isComplete && (
                <div className="tiny">
                  Finished. Everyone who helped has been paid — a new project starts next season.
                </div>
              )}
            </div>

            {giving && (
              <GiveForm
                onGive={(gift) =>
                  contribute({
                    coins: gift.coins ?? 0,
                    items: gift.items ?? [],
                    pokemonIds: gift.pokemonIds ?? [],
                  })
                }
                onCancel={() => setGiving(false)}
              />
            )}

            {project.leaderboard.length > 0 && (
              <div className="stack-sm">
                <div className="tiny mono muted">TOP CONTRIBUTORS</div>
                {project.leaderboard.map((row) => (
                  <div
                    key={row.rank}
                    className={`row row--between tiny ${row.isMe ? 'project__me' : ''}`}
                  >
                    <span>
                      <span className="muted mono">{row.rank}.</span>{' '}
                      <Link to={`/trainer/${row.username}`}>{row.username}</Link>
                    </span>
                    <span className="mono muted">{formatNumber(row.points)}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Panel>
  );
}
